/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import { Author } from "../../../basic/author.js";
import get from "lodash/get";
import { connect } from "react-redux";
import { AccessType } from "@quintype/components";
import PropTypes from "prop-types";
import { HeroImage } from "../../../atoms/hero-image";
import { HeroImageCaption } from "../../../atoms/hero-image-caption";
import { StorySubheadline } from "../../../atoms/story-subheadline";
import { Sidebar } from "../../../molecules/sidebar";
import { StoryFooter } from "../../../molecules/story-footer";
import { StoryHeadline } from "../../../atoms/story-headline";
import { StorySection } from "../../../atoms/story-section";
import { AmazonPolly } from "../../../amazon-polly/amazon-polly";
import { Share } from "../../../molecules/share";
import { StoryTags } from "../../../story-page-components/story-tags.js";
import {
  getSocialShareStyle,
  getOverride,
  gridSelectorTemplate,
  accessCheck,
  getInfiniteScrollConfigStyle
} from "../../../../utils";
import { getAccessData, getStoryCards, handleAccess, checkIfStoryPublic } from "../../../sso-components/sso-utils.js";
import { ImageFallback } from "../../../basic/image-fallback";
import { addTargetBlankToExtLinks } from "../../../../../utils/add-target-blank-to-extlinks.js";
import {
  sectionGrid,
  headlineGrid,
  subheadlineGrid,
  amazonPollyGrid,
  imageGrid,
  imageCaptionGrid,
  authorGrid,
  metypeGrid,
  socialTagsGrid,
  relatedStoriesGrid,
  horizontalLineGrid
} from "../../story-selectors";

import "./listicle.m.css";

function ListicleStoryTemplate({
  story,
  config,
  preview,
  access,
  accessLoading,
  firstStory,
  getStoryCardTemplate,
  publisherConfig,
  index
}) {
  const socialShareStyle = getSocialShareStyle(config);
  const isStoryPublic = checkIfStoryPublic(story);
  const haveFocusPoint = !!get(story, ["hero-image-metadata", "focus-point"]);
  const listicleStory = get(config, ["theme-attributes", "listicle-story"], "default");
  const infiniteScrollStyle = getInfiniteScrollConfigStyle(config);
  const cdnImage = get(config, ["cdn-image"], "");
  const listicleList = {
    "hero-priority": true,
    "headline-priority": true,
    "headline-hero-priority": true,
    "headline-sideway": false,
    default: false
  };
  const horizontalShare = listicleList[listicleStory] && socialShareStyle.includes("horizontal");
  const {
    "last-published-at": date,
    "first-published-at": firstpublishedTime,
    cards: storyCards,
    "bullet-type": bulletType = [],
    authors: authorsArray = [
      {
        name: story["author-name"],
        id: story["author-id"]
      }
    ],
    headline = "",
    subheadline = "",
    id: storyId
  } = story;

  const showPolly = get(access, [storyId, "granted"], false);
  const cardLength =
    get(story, ["cards", 0, "card-type"]) === "introduction" ? storyCards.length : storyCards.length + 1;

  const publishedTime = date || firstpublishedTime;

  const aspectRatio = {
    "hero-priority": [16, 9],
    "headline-priority": [16, 9],
    "headline-hero-priority": [16, 9],
    "headline-sideway": [4, 3],
    "hero-overlay": [16, 9]
  };

  const desktopAspectRatio = new Proxy(aspectRatio, getOverride([8, 3]));

  const mobileAspectRatio = new Proxy({}, getOverride([16, 9]));

  const getImageTemplate = story => {
    const image = get(story, ["hero-image-s3-key"]);
    if (image) {
      return (
        <div styleName={`image-wrapper ${!haveFocusPoint ? "full-height" : ""}`}>
          <HeroImage
            story={story}
            desktopAspectRatio={desktopAspectRatio[listicleStory]}
            mobileAspectRatio={mobileAspectRatio[listicleStory]}
          />
        </div>
      );
    }

    if (listicleStory === "hero-overlay" && !image) {
      return (
        <div styleName="fallback-image">
          <ImageFallback />
        </div>
      );
    }

    return <div />;
  };

  // classes for template
  const listicleStoryClass = {
    "hero-priority": "hero-priority-template",
    "headline-priority": "headline-priority-template",
    "headline-hero-priority": "headline-hero-priority-template",
    "headline-sideway": "headline-sideway-temp",
    "hero-overlay": "hero-overlay-temp"
  };
  const getPhotoStoryClass = new Proxy(listicleStoryClass, getOverride(""));

  // bullet style
  const bulletStyle = {
    counterReset: bulletType === "321" ? `listicle ${cardLength}` : ""
  };
  const sideAdTemplate = {
    "hero-priority": false,
    "headline-hero-priority": false,
    "headline-sideway": false,
    "hero-overlay": false
  };
  const getSideAd = new Proxy(sideAdTemplate, getOverride(true));

  // Horizontal SocialShare
  const supportHorizontalSocialShareGrid = {
    default: "grid-col-7-10",
    "hero-priority": "grid-col-8-12",
    "headline-priority": "grid-col-8-10",
    "headline-hero-priority": "grid-col-9-12"
  };
  const gridHorizotalSocialShareStyle = supportHorizontalSocialShareGrid[listicleStory]
    ? supportHorizontalSocialShareGrid[listicleStory]
    : supportHorizontalSocialShareGrid["default"];

  const supportVerticalAuthorGrid = {
    default: "grid-col-7-10",
    "hero-priority": "grid-col-2-3 grid-row-3-auto",
    "headline-priority": "grid-col-2-3 grid-row-7-auto",
    "headline-hero-priority": "grid-col-2-3 grid-row-7-auto",
    "headline-sideway": "grid-col-2-3 grid-row-9-auto",
    "hero-overlay": "grid-col-2-3 grid-row-9-auto"
  };
  const gridVerticalAuthorStyle = supportVerticalAuthorGrid[listicleStory]
    ? supportVerticalAuthorGrid[listicleStory]
    : supportVerticalAuthorGrid["default"];

  const getSocialShare = listicleStory => {
    switch (listicleStory) {
      case "hero-priority":
        return (
          <Share
            story={story}
            config={config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "hero-overlay":
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} vertical />;
      case "headline-hero-priority":
        return (
          <Share
            story={story}
            config={config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "headline-priority":
        return (
          <Share
            story={story}
            config={config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "headline-sideway":
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} vertical />;
      default:
        return <Share story={story} config={config} grey={socialShareStyle.includes("grey")} />;
    }
  };
  const listicleStoryClassMappings = {
    "hero-overlay": "simple"
  };
  const section = listicleStoryClassMappings[listicleStory] || "";
  const readTime = get(story, ["read-time"]);
  function getAuthor(listicleStory, config) {
    switch (listicleStory) {
      case "hero-priority":
        return (
          <Author
            config={config}
            authors={authorsArray}
            isLink
            showDateOnly
            date={publishedTime}
            story={listicleStory}
            readTime={readTime}
          />
        );
      case "hero-overlay":
        return (
          <Author
            listicleStory={listicleStory}
            config={config}
            authors={authorsArray}
            isLink
            showDateOnly
            date={publishedTime}
            story={listicleStory}
            readTime={readTime}
          />
        );
      default:
        return (
          <Author config={config} authors={authorsArray} isLink showDateOnly date={publishedTime} readTime={readTime} />
        );
    }
  }

  return (
    <div
      style={bulletStyle}
      styleName={`listicle-wrapper ${getPhotoStoryClass[listicleStory]} ${horizontalShare ? "horizontal" : ""}`}
      className="blank-story grid"
    >
      <div
        styleName="hero-image"
        data-testid="listicle-hero-image"
        className={gridSelectorTemplate(imageGrid, listicleStory)}
      >
        {getImageTemplate(story)}
      </div>
      <div styleName="caption" className={gridSelectorTemplate(imageCaptionGrid, listicleStory)}>
        <HeroImageCaption story={story} />
      </div>
      <div styleName="section item" className={gridSelectorTemplate(sectionGrid, listicleStory)}>
        <StorySection story={story} config={config} template={section} />
      </div>
      <div styleName="headline item" className={gridSelectorTemplate(headlineGrid, listicleStory)}>
        <StoryHeadline headline={headline} headlineWhite={listicleStory === "hero-overlay"} />
      </div>
      <div styleName="summary item" className={gridSelectorTemplate(subheadlineGrid, listicleStory)}>
        <StorySubheadline subheadline={subheadline} subheadlineWhite={listicleStory === "hero-overlay"} />
      </div>
      {showPolly && (
        <div styleName="amazon-polly item" className={gridSelectorTemplate(amazonPollyGrid, listicleStory)}>
          <AmazonPolly story={story} customClass="amazon-polly-custom-selector" />
        </div>
      )}

      <div styleName="author" className={gridSelectorTemplate(authorGrid, listicleStory)}>
        {getAuthor(listicleStory)}
      </div>
      <div
        styleName="social-share"
        className={`${horizontalShare ? gridHorizotalSocialShareStyle : gridVerticalAuthorStyle}`}
      >
        <div styleName="sticky">{getSocialShare(listicleStory)}</div>
      </div>
      <div
        data-testid="horizontal-line"
        styleName="horizontal-line"
        className={gridSelectorTemplate(horizontalLineGrid, listicleStory)}
      />

      {/* Story-cards */}

      {isStoryPublic
        ? getStoryCards(storyCards, config, story, cdnImage, getStoryCardTemplate, firstStory)
        : handleAccess(story, access, accessLoading, config, getStoryCardTemplate, firstStory)}
      {(infiniteScrollStyle !== "read-more" || (infiniteScrollStyle === "read-more" && firstStory)) && (
        <>
          <div styleName="tags" className={gridSelectorTemplate(socialTagsGrid, listicleStory)}>
            <StoryTags tags={story.tags} config={config} />
          </div>
          <div styleName="sidebar" className={gridSelectorTemplate(relatedStoriesGrid, listicleStory)}>
            <Sidebar
              story={story}
              showAd={getSideAd[listicleStory]}
              template={listicleStory}
              wide
              publisherConfig={publisherConfig}
              config={config}
              index={index}
            />
          </div>
          <div styleName="footer" className={gridSelectorTemplate(metypeGrid, listicleStory)}>
            {!preview && <StoryFooter story={story} />}
          </div>
        </>
      )}
    </div>
  );
}
ListicleStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  access: PropTypes.object,
  accessLoading: PropTypes.bool,
  firstStory: PropTypes.bool,
  getStoryCardTemplate: PropTypes.func,
  publisherConfig: PropTypes.object,
  index: PropTypes.number
};

class ListicleStoryAccessWrapper extends React.Component {
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    const getAccess = () => getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    this.props.initAccessType(getAccess);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }

  render() {
    const storyMetadata = get(this.props, ["story", "metadata"], {});
    return (
      <div className={`story-grid blank-story`} ref={el => (this.storyWrapperElement = el)}>
        <div style={{ display: "none" }} className="blank-story-metadata">
          {JSON.stringify(storyMetadata)}
        </div>
        {/* DO NOT DELETE, HACK ON PURPOSE. */}
        <ListicleStoryTemplate {...this.props} />
      </div>
    );
  }
}
ListicleStoryAccessWrapper.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  checkAccess: PropTypes.func,
  initAccessType: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func
};

class ListicleStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");
    const storyId = get(this.props, ["story", "id"]);

    const themeAttributes = get(this.props, ["config", "theme-attributes"]);
    const PbAccessTypeConfig = get(this.props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
    const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
    const accessTypeBkIntegrationId = get(
      PbAccessTypeConfig,
      ["bridgeKeeperIntegrationId"],
      get(themeAttributes, ["accesstypeBkIntegrationId"], "")
    );
    const isStaging =
      get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
      get(themeAttributes, ["accesstypeEnvironment"]) === "staging";

    const enableAccesstype = get(
      PbAccessTypeConfig,
      ["enableAccesstype"],
      get(themeAttributes, ["enableAccesstype"], false)
    );
    const disableMetering = get(
      PbAccessTypeConfig,
      ["disableMetering"],
      get(themeAttributes, ["disableMetering"]) === "true"
    );

    const accessTypeProdHost = get(PbAccessTypeConfig, ["accessTypeProdHost"]);

    const accessTypeStgHost = get(PbAccessTypeConfig, ["accessTypeStgHost"]);

    return (
      <AccessType
        enableAccesstype={accessCheck(enableAccesstype)}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey}
        accessTypeBkIntegrationId={accessTypeBkIntegrationId}
        email={email}
        phone={phone}
        disableMetering={disableMetering}
        storyId={storyId}
        prodHost={accessTypeProdHost}
        stagingHost={accessTypeStgHost}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
            <ListicleStoryAccessWrapper
              accessIsLoading={accessIsLoading}
              accessUpdated={accessUpdated}
              initAccessType={initAccessType}
              checkAccess={checkAccess}
              {...this.props}
            />
          </div>
        )}
      </AccessType>
    );
  }
}
ListicleStoryBase.propTypes = {
  config: PropTypes.object
};
const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true),
  publisherConfig: get(state, ["qt", "data", "publisherConfig"], {})
});

export const ListicleStory = connect(mapStateToProps)(ListicleStoryBase);
export default ListicleStory;
